/**
 * Blur utility functions
 */

import { BLUR_LEVELS } from './constants';

/**
 * Apply pixelation blur to a canvas region
 */
export function applyPixelBlur(ctx, x, y, w, h, pixelSize) {
  if (w <= 0 || h <= 0) return;

  x = Math.max(0, Math.floor(x));
  y = Math.max(0, Math.floor(y));
  w = Math.min(ctx.canvas.width - x, Math.floor(w));
  h = Math.min(ctx.canvas.height - y, Math.floor(h));

  if (w <= 0 || h <= 0) return;

  try {
    const imgData = ctx.getImageData(x, y, w, h);
    const data = imgData.data;

    for (let py = 0; py < h; py += pixelSize) {
      for (let px = 0; px < w; px += pixelSize) {
        let r = 0, g = 0, b = 0, count = 0;

        for (let dy = 0; dy < pixelSize && py + dy < h; dy++) {
          for (let dx = 0; dx < pixelSize && px + dx < w; dx++) {
            const i = ((py + dy) * w + (px + dx)) * 4;
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count++;
          }
        }

        r = Math.floor(r / count);
        g = Math.floor(g / count);
        b = Math.floor(b / count);

        for (let dy = 0; dy < pixelSize && py + dy < h; dy++) {
          for (let dx = 0; dx < pixelSize && px + dx < w; dx++) {
            const i = ((py + dy) * w + (px + dx)) * 4;
            data[i] = r;
            data[i + 1] = g;
            data[i + 2] = b;
          }
        }
      }
    }

    ctx.putImageData(imgData, x, y);
  } catch (err) {
    console.error('Blur error:', err);
  }
}

/**
 * Download a single file with blur applied
 */
export function downloadFile(file, onSuccess, onError) {
  if (!file) {
    onError?.('File not found');
    return;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();

  img.crossOrigin = 'anonymous';

  img.onload = () => {
    try {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // Apply blur to enabled regions
      Object.entries(file.blurSettings || {}).forEach(([id, settings]) => {
        if (!settings?.enabled) return;

        const person = file.detections.find(d => d.id === id);
        if (!person) return;

        const pixelSize = BLUR_LEVELS[settings.level]?.pixelSize || 22;
        const target = settings.target || 'face';

        // Blur face
        if ((target === 'face' || target === 'both') && person.faceBox) {
          const { x, y, w, h } = person.faceBox;
          applyPixelBlur(
            ctx,
            (x / 100) * img.width,
            (y / 100) * img.height,
            (w / 100) * img.width,
            (h / 100) * img.height,
            pixelSize
          );
        }

        // Blur body
        if ((target === 'body' || target === 'both') && person.bodyBox) {
          const { x, y, w, h } = person.bodyBox;
          applyPixelBlur(
            ctx,
            (x / 100) * img.width,
            (y / 100) * img.height,
            (w / 100) * img.width,
            (h / 100) * img.height,
            pixelSize
          );
        }
      });

      // Apply blur for custom blur boxes
      (file.customBlurBoxes || []).forEach((customBox) => {
        if (!customBox?.enabled) return;

        const pixelSize = BLUR_LEVELS[customBox.level]?.pixelSize || 22;
        const { x, y, w, h } = customBox.box;

        applyPixelBlur(
          ctx,
          (x / 100) * img.width,
          (y / 100) * img.height,
          (w / 100) * img.width,
          (h / 100) * img.height,
          pixelSize
        );
      });

      // Create download
      const filename = file.name.replace(/\.[^.]+$/, '') + '_blurred.png';

      canvas.toBlob((blob) => {
        if (!blob) {
          onError?.('Failed to create image blob');
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 2000);

        onSuccess?.(filename);
      }, 'image/png', 1.0);

    } catch (err) {
      console.error('Download error:', err);
      onError?.(err.message);
    }
  };

  img.onerror = () => {
    onError?.('Failed to load image');
  };

  img.src = file.preview;
}
